/**
 * Alice Framework — what Alice can actually do in this portal.
 *
 * Read-only, and deliberately unflattering: Cascade Alice has no tools, so the
 * Tools section says so and explains what she does instead. The page exists to
 * be built against — register a tool in the Worker's copilot registry and it
 * appears here, so the page can never claim a capability the Worker does not
 * have.
 *
 * Everything on screen comes from GET /api/copilot/registry, which derives
 * connection status from the Worker's own environment.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Cable,
  Check,
  MessageSquareText,
  ShieldCheck,
  Wrench,
  X,
} from 'lucide-react';
import type { AliceRegistryResponse } from '@command-center/core-platform';
import { Badge, Card, usePageHeader } from '@command-center/ui-system';
import { request } from '../lib/api';
import { adminConfig } from '../platform/config';
import { ErrorState, LoadingState, formatDateTime } from './parts';

export function AliceFrameworkView() {
  const registry = useQuery({
    queryKey: ['copilot', 'registry'],
    queryFn: () => request<AliceRegistryResponse>('/api/copilot/registry'),
    staleTime: 60_000,
  });
  /*
    The shell prints the page name; the heading block that used to restate it
    here — eyebrow, title, and paragraph — was the same duplicate-title problem
    the feeds already fixed.
  */
  usePageHeader(
    {
      title: 'Alice Framework',
      subtitle: "Alice's tools, connections, prompts, and the policy in force for Cascade.",
    },
    [],
  );

  if (registry.isLoading) return <LoadingState />;
  if (registry.isError) return <ErrorState error={registry.error} />;

  const data = registry.data;
  const connections = data?.connections ?? [];
  const tools = data?.tools ?? [];
  const prompts = data?.prompts ?? [];
  const policy = data?.effectivePolicy;
  const configured = connections.filter((item) => item.status === 'configured').length;

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          The Worker is the source of truth — this page reports what is deployed, not what is
          planned.
        </p>
        <Link
          to="/settings"
          className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back to Settings
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Tools" value={tools.length} hint={tools.length === 0 ? 'None yet' : undefined} />
        <Stat label="Connections configured" value={`${configured}/${connections.length}`} />
        <Stat label="Chat model" value={policy?.defaultModel ?? adminConfig.alice?.defaultModelLabel ?? '—'} />
      </div>

      {/* The honest headline. Stated once, up top, rather than implied by an
          empty list the reader has to interpret. */}
      <Card className="border-primary/30 bg-primary/[0.035] p-4">
        <div className="flex items-start gap-3">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <div className="text-sm">
            <p className="font-medium text-foreground">Where Alice is today</p>
            <p className="mt-1 leading-relaxed text-muted-foreground">
              She reads the one record you have open — lead, contact, or organization — as a field
              list appended to her system prompt, and can summarize it, draft outreach, and suggest
              a next step. She has <span className="font-medium text-foreground">no callable tools</span>,
              cannot search, cannot open a second record, and cannot write anything. Sends and
              record changes go through the reviewed LeadLoop workflows instead.
            </p>
          </div>
        </div>
      </Card>

      <Section icon={Wrench} title="Tools" count={tools.length}>
        {tools.length === 0 ? (
          <EmptyPanel>
            No tools are registered. Add one to the Cascade Worker&rsquo;s copilot registry
            (<code className="rounded bg-muted px-1 py-0.5 text-[11px]">routes/copilot-registry.ts</code>)
            and it appears here with its data source, access level, and whether it needs approval.
          </EmptyPanel>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {tools.map((tool) => (
              <Card key={tool.name} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{tool.displayName ?? tool.name}</p>
                  <Badge variant={tool.access === 'write' ? 'warning' : 'secondary'}>
                    {tool.access}
                  </Badge>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tool.description}</p>
                <p className="mt-2 text-[11px] text-muted-foreground">{tool.dataSource}</p>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Cable} title="Connections" count={connections.length}>
        <div className="overflow-hidden rounded-lg border border-border">
          {connections.map((item) => (
            <div
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2.5 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{item.label}</p>
                {item.detail && (
                  <p className="truncate text-[11px] text-muted-foreground">{item.detail}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="muted" className="uppercase tracking-wide">{item.kind}</Badge>
                {item.status === 'configured' ? (
                  <span className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Not configured
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={MessageSquareText} title="Prompts" count={prompts.length}>
        <div className="flex flex-col gap-2">
          {prompts.map((prompt) => (
            <Card key={prompt.id} className="p-3">
              <p className="text-sm font-medium text-foreground">{prompt.label}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{prompt.description}</p>
              {prompt.preview && (
                <p className="mt-2 rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                  {prompt.preview}
                </p>
              )}
            </Card>
          ))}
        </div>
      </Section>

      {policy && (
        <Section icon={ShieldCheck} title="Policy in force">
          <Card className="p-4">
            <p className="text-sm leading-relaxed text-foreground">{policy.notes}</p>
            <dl className="mt-3 grid gap-x-6 gap-y-3 text-[12px] sm:grid-cols-3">
              <PolicyField label="Portal" value={policy.portalKey} />
              <PolicyField label="Role" value={policy.role} />
              <PolicyField label="Max risk level" value={String(policy.maxRiskLevel)} />
              <PolicyField
                label="Approval required at"
                value={`Risk ${policy.approvalRequiredAtRiskLevel}`}
              />
              <PolicyField
                label="Disabled connectors"
                value={policy.disabledConnectors.join(', ') || 'None'}
              />
              <PolicyField
                label="Enabled tools"
                value={policy.enabledTools?.length ? policy.enabledTools.join(', ') : 'None'}
              />
            </dl>
            <p className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground">
              Policies live in code (<code className="rounded bg-muted px-1 py-0.5">core-platform/alice-policy.ts</code>),
              not in this page — there is no editor by design.
            </p>
          </Card>
        </Section>
      )}

      {data?.generatedAt && (
        <p className="text-[11px] text-muted-foreground">
          Catalog generated {formatDateTime(data.generatedAt)}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </Card>
  );
}

function Section({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof Wrench;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        {title}
        {count !== undefined && (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            {count}
          </span>
        )}
      </h3>
      {children}
    </section>
  );
}

function EmptyPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm leading-relaxed text-muted-foreground">
      {children}
    </div>
  );
}

function PolicyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 break-words text-foreground">{value}</dd>
    </div>
  );
}
